import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  CircularProgress,
  Backdrop,
  Link,
  Paper,
  Pagination,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import UploadIcon from "@mui/icons-material/Upload";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LoopIcon from "@mui/icons-material/Loop";
import UploadField from "./UploadField";
import MediaItem from "./MediaItem";
import CloseConfirmation from "./CloseConfirmation";
import GlobalErrorPrompt from "../../global/GlobalErrorPrompt";
import { TypographyLarge, TypographySmall } from "../../customComponent";
import {
  getAllAssets,
  setErrorHandler,
} from "../../../store/actions/cmsAction";
import { SET_CONTENT_DISABLE_DIRECT } from "../../../store/actionTypes/cmsActionType";
import { useDebounce } from "../../hooks";
import { colorStyling } from "../../../helpers/color";
import { logger } from "../../../helpers/logger";
import { parseVariables } from "../../../helpers/localize";
import { showSanitizedInput } from "../../../helpers/inputProcessing";
import { stellarFlag } from "../../../externalFeatures/componentsFlag";
import parser from "html-react-parser";

const DATA_FETCH_LIMIT = 50;
const DATA_PER_PAGE_LIMIT = 10;
const assetPerLayerLimit = process.env.REACT_APP_ASSET_PER_LAYER_LIMIT || 50;

const MediaPicker = ({
  open,
  handleClose,
  name,
  addContent,
  container,
  contentState,
  onlyOne,
  noUpload,
  oneAssetType,
}) => {
  const dispatch = useDispatch();
  const { media, mediaData, mediaFilter, errorHandler } = useSelector(
    (state) => state.cms
  );

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const { user } = useSelector((state) => state.auth);
  const permission = user?.permission;
  const assetPermission = permission?.asset;

  const contents = contentState?.[0];
  const [holdRefresh, setHoldRefresh] = useState(true);
  const [forceLoading, setForceLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [openCloseConfirmation, setOpenCloseConfirmation] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [dataFrom, setDataFrom] = useState(0);
  const [mediaOffset, setMediaOffset] = useState(0);
  const [searchBy, setSearchBy] = useState("name");
  const [searchInput, setSearchInput] = useState("");
  const [mediaType, setMediaType] = useState("");
  const [sortBy, setSortBy] = useState("idDesc");

  const [selectedMedias, setSelectedMedias] = useState({});
  const [onPage, setOnPage] = useState("");
  const [uploadedURLs, setUploadedURLs] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [updateFlag, setUpdateFlag] = useState(false);

  const handleSelectMedia = (selected) => {
    const updated = onlyOne ? {} : { ...selectedMedias };
    const updatedOrder = onlyOne ? [] : [...selectedOrder];
    updated[selected.id] = selected;
    updatedOrder.push(selected.id);
    setSelectedMedias(updated);
    setSelectedOrder(updatedOrder);
  };

  const handleUnselectMedia = (selected) => {
    const updated = { ...selectedMedias };
    delete updated[selected.id];
    const updatedOrder = selectedOrder.filter((val) => val !== selected.id);
    setSelectedMedias(updated);
    setSelectedOrder(updatedOrder);
  };

  const handleCloseUploadUI = () => {
    setOpenUpload(false);
    setOnPage("");
  };

  const closeCloseConfirmation = () => {
    setOpenCloseConfirmation(false);
  };

  const addUploadedURLs = (urlArray, reset = false) => {
    if (reset) {
      setUploadedURLs([]);
      return;
    }

    const updated = [...uploadedURLs, ...urlArray];
    setUploadedURLs(updated);
  };

  useEffect(() => {
    if (mediaFilter.searchBy) {
      setSearchBy(mediaFilter.searchBy);
    } else {
      setSearchBy("name");
    }

    if (mediaFilter.searchInput) {
      setSearchInput(mediaFilter.searchInput);
    } else {
      setSearchInput("");
    }

    if (mediaFilter.mediaType) {
      setMediaType(mediaFilter.mediaType);
    } else {
      setMediaType("");
    }

    if (mediaFilter.sortBy) {
      setSortBy(mediaFilter.sortBy);
    } else {
      setSortBy("idDesc");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFilter]);

  const handleCloseDialog = async () => {
    if (onPage === "") {
      handleClose();
      setTimeout(() => handleCloseUploadUI(), 1111);
    } else {
      setOpenCloseConfirmation(true);
    }
  };

  const fetchAllAssets = (payload, filterUsed) => {
    let { limit, offset, type, byLike, input, append, sort } = payload;
    if (limit === undefined) limit = DATA_FETCH_LIMIT;
    if (offset === undefined) offset = mediaOffset;
    if (type === undefined) type = mediaType;
    if (sort === undefined) sort = sortBy;
    if (byLike === undefined) byLike = searchBy;
    if (input === undefined) input = searchInput;

    const getPayload = {
      payload: {
        limit,
        offset,
        type,
        sortBy: sort,
        [`${byLike}Like`]: input,
      },
      cache: media,
      append,
      filter: filterUsed,
    };

    setHoldRefresh(true);
    dispatch(
      getAllAssets(getPayload, () => {
        setLoadingSearch(false);
        setForceLoading(false);
        setTimeout(() => {
          setHoldRefresh(false);
        }, 5000);
      })
    );
  };

  const updateDataList = (page = 0, filterUsed = false) => {
    const nextSlice = page * DATA_PER_PAGE_LIMIT;
    setDataFrom(nextSlice);

    let appendData = false;
    if (media.length > 0 && nextSlice >= media.length) {
      appendData = true;
    }

    const nextOffset =
      Math.floor(nextSlice / DATA_FETCH_LIMIT) * DATA_FETCH_LIMIT;
    setMediaOffset(nextOffset);
    const payload = { offset: nextOffset, append: appendData };
    fetchAllAssets(payload, filterUsed);
  };

  const debouncedSearch = useDebounce(searchInput, 500);
  useEffect(
    () => {
      if (forceLoading) {
        return;
      }

      setDataFrom(0);
      setCurrentPage(0);
      setMediaOffset(0);
      if (open) {
        setLoadingSearch(true);
        fetchAllAssets({ offset: 0, input: debouncedSearch }, true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch] // Only call effect if debounced interval changes
  );

  const handleSearchInputChange = (event) => {
    const val = event.target.value;
    setSearchInput(val);
  };

  const handleSearchByChange = (event) => {
    const val = event.target.value;
    setSearchBy(val);

    if (searchInput?.length === 0) {
      return;
    }

    setDataFrom(0);
    setCurrentPage(0);
    setMediaOffset(0);
    setForceLoading(true);
    fetchAllAssets({ offset: 0, byLike: val }, true);
  };

  const handleMediaTypeChange = (event) => {
    const val = event.target.value;
    setMediaType(val);

    setDataFrom(0);
    setCurrentPage(0);
    setMediaOffset(0);
    setForceLoading(true);
    fetchAllAssets({ offset: 0, type: val }, true);
  };

  const handleSortByChange = (event) => {
    const val = event.target.value;
    setSortBy(val);

    setDataFrom(0);
    setCurrentPage(0);
    setMediaOffset(0);
    setForceLoading(true);
    fetchAllAssets({ offset: 0, sort: val }, true);
  };

  const handleResetFilter = async () => {
    setForceLoading(true);
    setSearchBy("name");
    setSearchInput("");
    setMediaType("");
    setSortBy("idDesc");

    setDataFrom(0);
    setCurrentPage(0);
    setMediaOffset(0);
    fetchAllAssets(
      {
        offset: 0,
        byLike: "name",
        input: "",
        type: "",
        sort: "idDesc",
      },
      true
    );
  };

  const handleCloseConfirmation = () => {
    uploadedURLs.forEach((url) => {
      URL.revokeObjectURL(url);
    });

    handleClose();
    setTimeout(() => handleCloseUploadUI(), 1111);
    setOpenCloseConfirmation(false);
    setUploadedURLs([]);
  };

  const handleSubmitGroupAsset = () => {
    if (contents?.length + 1 > assetPerLayerLimit) {
      dispatch(setErrorHandler({ limitReached: true }));
      return;
    }
    const options = { grouped: true };
    addContent(selectedMedias, selectedOrder, options);
    setSelectedMedias({});
    setSelectedOrder([]);
  };

  const handleSubmitContent = () => {
    if (selectedOrder?.length + contents?.length > assetPerLayerLimit) {
      dispatch(setErrorHandler({ limitReached: true }));
      return;
    }
    addContent(selectedMedias, selectedOrder);
    setSelectedMedias({});
    setSelectedOrder([]);
  };

  useEffect(() => {
    if (container.type === "media") {
      setForceLoading(true);
      updateDataList();
      setTimeout(() => {
        setHoldRefresh(false);
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      dispatch({ type: SET_CONTENT_DISABLE_DIRECT, payload: true });
      if (!holdRefresh) {
        updateDataList(currentPage, true);
      }
    } else {
      setOpenUpload(false);
      dispatch({ type: SET_CONTENT_DISABLE_DIRECT, payload: false });
      setSelectedMedias({});
      setSelectedOrder([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (oneAssetType?.length > 0) {
      setMediaType(oneAssetType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneAssetType]);

  const debouncedUpdate = useDebounce(currentPage, 1111);
  useEffect(() => {
    if (updateFlag === true) {
      updateDataList(currentPage);
      setUpdateFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUpdate]);

  const updateDataSlice = ({ page }) => {
    const nextPage = page >= 0 ? page : currentPage;
    const nextSlice = nextPage * DATA_PER_PAGE_LIMIT;
    if (nextSlice >= media.length) {
      setForceLoading(true);
    }

    setDataFrom(nextSlice);
    setCurrentPage(nextPage);
    setUpdateFlag(true);
  };

  const handleChangePagination = (e, page) => {
    updateDataSlice({ page: page - 1 });
  };

  const dataSliced = useMemo(() => {
    const lastIndex = dataFrom + DATA_PER_PAGE_LIMIT;
    logger.log(`[${dataFrom}, ${lastIndex}]`);
    const data = media.slice(dataFrom, lastIndex);
    if (data[0]?.type === "null") {
      setForceLoading(true);
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, dataFrom]);

  const parseDialogTitle = useMemo(() => {
    if (!openUpload) {
      return t("media.pickerTitle");
    } else {
      switch (onPage) {
        default:
        case "":
          return t("media.uploadNewTitle");

        case "confirm":
          return t("media.uploadConfirmationTitle");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, openUpload, onPage]);

  const parseNoSearchResults = useMemo(() => {
    if (searchInput.length === 0) {
      return <strong>{t("media.noData")}</strong>;
    } else {
      return (
        <>
          {parser(
            parseVariables(t("media.noResultsMatching"), {
              searchInput: showSanitizedInput(searchInput),
            })
          )}
        </>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSearch]);

  const parseNoMoreSearchResults = useMemo(() => {
    if (searchInput.length === 0) {
      return <strong>{parser(t("media.noMoreData"))}</strong>;
    } else {
      return (
        <>
          {parser(
            parseVariables(t("media.noMoreResultsMatching"), {
              searchInput: showSanitizedInput(searchInput),
            })
          )}
        </>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSearch]);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={true}
      onClose={() => {
        if (onPage === "") {
          handleCloseDialog();
          return;
        }
      }}
    >
      <GlobalErrorPrompt />

      <CloseConfirmation
        open={openCloseConfirmation}
        closeDialog={closeCloseConfirmation}
        handleConfirm={handleCloseConfirmation}
      />

      <DialogTitle
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        {parseDialogTitle}
        <IconButton onClick={() => handleCloseDialog()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: `55vh` }}>
        <>
          {!openUpload ? (
            <Box>
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={1}
                sx={{ mt: 1, px: 2 }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box>
                    <TextField
                      size="small"
                      label={t("media.searchByLabel")}
                      select
                      SelectProps={{ native: true }}
                      value={searchBy}
                      disabled={forceLoading}
                      onChange={handleSearchByChange}
                      sx={{ width: "150px" }}
                    >
                      <option key="name" value="name">
                        {t("media.searchByName")}
                      </option>
                      <option key="campaignName" value="campaignName">
                        {t("media.searchByCampaignName")}
                      </option>
                    </TextField>
                  </Box>
                </Box>

                <Box sx={{ width: "100%" }}>
                  <TextField
                    onChange={handleSearchInputChange}
                    value={searchInput}
                    fullWidth
                    type="Search"
                    placeholder={t("media.searchPlaceholder")}
                    size="small"
                    disabled={forceLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box>
                  <TextField
                    size="small"
                    label={t("media.searchTypeLabel")}
                    select
                    SelectProps={{ native: true }}
                    value={mediaType}
                    disabled={forceLoading}
                    onChange={handleMediaTypeChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: "150px" }}
                  >
                    {oneAssetType?.length > 0 ? (
                      <option key={oneAssetType} value={oneAssetType}>
                        {t(`media.searchType${oneAssetType}`)}
                      </option>
                    ) : (
                      <>
                        <option key="default" value="" defaultValue>
                          {t("media.searchTypeDefault")}
                        </option>
                        <option key="IMAGE" value="IMAGE">
                          {t("media.searchTypeIMAGE")}
                        </option>
                        <option key="VIDEO" value="VIDEO">
                          {t("media.searchTypeVIDEO")}
                        </option>
                      </>
                    )}
                  </TextField>
                </Box>

                <Box>
                  <TextField
                    size="small"
                    label={t("media.sortByLabel")}
                    select
                    SelectProps={{ native: true }}
                    value={sortBy}
                    disabled={forceLoading}
                    onChange={handleSortByChange}
                    sx={{ width: "150px" }}
                  >
                    <option key="idAsc" value="idAsc">
                      {t("media.sortByOldest")}
                    </option>
                    <option key="idDesc" value="idDesc">
                      {t("media.sortByNewest")}
                    </option>
                  </TextField>
                </Box>

                <Box>
                  <IconButton
                    size="medium"
                    disabled={forceLoading}
                    onClick={handleResetFilter}
                    sx={{
                      border: 1,
                      borderColor: grey[400],
                      color: colorStyling.black,
                      ":hover": {
                        borderColor: colorStyling.black,
                        backgroundColor: grey[100],
                      },
                    }}
                  >
                    <Tooltip title={tCommon("resetFilterTip")}>
                      <RestartAltIcon fontSize="small" />
                    </Tooltip>
                  </IconButton>
                </Box>
              </Box>

              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={{ mt: 1 }}
              >
                {(media.length > 0 && dataSliced?.length === 0) ||
                dataSliced[0]?.type === "null" ||
                forceLoading ||
                loadingSearch ? (
                  <Box
                    alignContent={"center"}
                    textAlign={"center"}
                    sx={{ py: 2 }}
                    width={"100%"}
                    height={"33vw"}
                  >
                    <LoopIcon
                      color="inherit"
                      sx={{
                        fontSize: "55px",
                        animation: "spin 2s linear infinite",
                        "@keyframes spin": {
                          "0%": {
                            transform: "rotate(360deg)",
                          },
                          "100%": {
                            transform: "rotate(0deg)",
                          },
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <Box width={"100%"}>
                    {media.length > 0 ? (
                      <>
                        {mediaData?.items?.length === 0 ? (
                          <Box
                            alignContent={"center"}
                            textAlign={"center"}
                            sx={{ py: 2 }}
                            height={"33vw"}
                          >
                            <LoopIcon
                              color="inherit"
                              sx={{
                                fontSize: "55px",
                                animation: "spin 2s linear infinite",
                                "@keyframes spin": {
                                  "0%": {
                                    transform: "rotate(360deg)",
                                  },
                                  "100%": {
                                    transform: "rotate(0deg)",
                                  },
                                },
                              }}
                            />
                          </Box>
                        ) : (
                          <Grid
                            container
                            columns={5}
                            direction={"row"}
                            sx={{
                              py: 1,
                              px: 3,
                              height: "400px",
                              alignItems: "flex-start",
                              justifyContent: "flex-start",
                            }}
                          >
                            {dataSliced.length > 0 ? (
                              dataSliced.map((m) => (
                                <Grid
                                  item
                                  xs={1}
                                  key={m.id}
                                  sx={{
                                    placeItems: "center",
                                  }}
                                >
                                  <MediaItem
                                    media={m}
                                    order={selectedOrder.indexOf(m.id)}
                                    selectedMedia={selectedMedias[m.id]}
                                    handleSelectMedia={handleSelectMedia}
                                    handleUnselectMedia={handleUnselectMedia}
                                    onlyOne={onlyOne}
                                  />
                                </Grid>
                              ))
                            ) : (
                              <Grid></Grid>
                            )}
                          </Grid>
                        )}
                      </>
                    ) : (
                      <Box
                        height={"33vw"}
                        alignContent={"center"}
                        textAlign={"center"}
                      >
                        {mediaOffset === 0 ? (
                          <TypographyLarge>
                            {parseNoSearchResults}
                          </TypographyLarge>
                        ) : (
                          <TypographyLarge>
                            {parseNoMoreSearchResults}
                          </TypographyLarge>
                        )}

                        {mediaOffset === 0 && (
                          <TypographyLarge>
                            {t("media.searchResetHintPrefix")}
                            <Link href="#" onClick={handleResetFilter}>
                              {t("media.searchResetClickHere")}
                            </Link>
                            {t("media.searchResetHintSuffix")}
                          </TypographyLarge>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {dataSliced?.length > 0 && !forceLoading && !loadingSearch && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Paper component="div" elevation={1} sx={{ px: 1 }}>
                    <Pagination
                      showFirstButton
                      showLastButton
                      page={currentPage + 1}
                      count={Math.ceil(mediaData.totalItem / 10)}
                      onChange={handleChangePagination}
                      disabled={forceLoading}
                    />
                  </Paper>
                </Box>
              )}
            </Box>
          ) : (
            <>
              <GlobalErrorPrompt />

              <UploadField
                setOnPage={setOnPage}
                closeMediaPicker={handleClose}
                closeUploadUI={handleCloseUploadUI}
                container={container}
                contentState={contentState}
                setLoadingFile={setLoadingFile}
                uploadedURLs={uploadedURLs}
                addUploadedURLs={addUploadedURLs}
              />
            </>
          )}
        </>

        {assetPermission?.create && !openUpload && !noUpload && (
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"end"}
            sx={{ mt: 2 }}
          >
            <Button
              sx={{
                borderRadius: "16px",
                boxShadow: "0px 13px 10px -10px rgba(0,0,0,0.4)",
              }}
              variant="contained"
              startIcon={<UploadIcon fontSize="small" />}
              onClick={() => setOpenUpload(true)}
            >
              {t("media.uploadNewBtn")}
            </Button>
          </Box>
        )}
      </DialogContent>

      {selectedOrder?.length > 0 && !openUpload && (
        <TypographySmall
          sx={{
            mr: 3,
            color: "blue",
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          {parseVariables(t("media.mediaCountHint"), {
            mediaCount: selectedOrder.length,
          })}
        </TypographySmall>
      )}

      {errorHandler.selectedMedia && (
        <TypographySmall
          sx={{
            mr: 3,
            mt: "2px",
            color: "red",
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          {t("media.mediaNoneHint")}
        </TypographySmall>
      )}

      {errorHandler.limitReached && (
        <TypographySmall
          sx={{
            mr: 3,
            mt: "2px",
            color: "red",
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          {parseVariables(t("error.addLimitPerLayerReached"), {
            maxCount: assetPerLayerLimit - contents.length,
            limit: assetPerLayerLimit,
            currentCount: contents.length,
          })}
        </TypographySmall>
      )}

      {errorHandler.assetDuplicated && (
        <TypographySmall
          sx={{
            mr: 3,
            mt: "2px",
            color: "red",
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          {t("media.addGroupedButDuplicatedError")}
        </TypographySmall>
      )}

      <DialogActions sx={{ mx: 2, mb: 1 }}>
        {onPage !== "" ? null : (
          <>
            <Button variant="outlined" onClick={() => handleCloseDialog()}>
              {tCommon("cancelBtn")}
            </Button>

            {stellarFlag && !noUpload && selectedOrder?.length > 1 && (
              <Button
                variant="contained"
                color="warning"
                onClick={handleSubmitGroupAsset}
              >
                {t("media.addGroupedBtn")}
              </Button>
            )}

            <Button variant="contained" onClick={handleSubmitContent}>
              {!noUpload ? t("media.addMediaBtn") : tCommon("submitBtn")}
            </Button>
          </>
        )}
      </DialogActions>

      <Backdrop
        sx={{
          color: colorStyling.white.inactive,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loadingFile}
        onClick={null}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default MediaPicker;
